/**
 * Gateway health meter — "is this TAB keeping up with its feed?"
 *
 * Every server hop on node1 was measured at ≤ 25 ms (2026-09-24) while a
 * trader still saw an alt-line book take seconds. The remaining hop is the
 * browser: frames queue in the tab's WebSocket message queue while the main
 * thread parses, ingests and re-renders. Nothing measured that, so this does:
 *
 *   skew      the gateway's clock vs ours, from ping → pong (`serverTs` at the
 *             midpoint of the round trip). EWMA; carries ±rtt/2 asymmetry.
 *   queueLag  for each `alive` beat (sent every 5 s with `serverTs`):
 *             receipt − serverTs − skew = network + time the beat waited in
 *             the tab's queue behind frames the main thread had not processed.
 *             A busy tab shows this climbing while the server's `/stats`
 *             shows `bufferedBytes 0`.
 *   fps       market + book frames dispatched per second (5 s window).
 *   flushP95  the store's actual flush interval, p95 over the last ~10 s. The
 *             cap is 33 ms; a main thread that cannot keep 30 Hz shows here.
 *
 * Dependency-free on purpose: the store, the client and the status bar all
 * import it; it imports nothing.
 */

export interface GatewayHealthSnapshot {
  rttMs: number | null;
  skewMs: number | null;
  queueLagMs: number | null;
  framesPerSec: number;
  flushP95Ms: number | null;
  /** Epoch ms of the last update (alive or pong). 0 = never. */
  at: number;
}

const FPS_WINDOW_MS = 5_000;
const FLUSH_RING = 300; // ≈ 10 s at 30 Hz

export class GatewayHealthMeter {
  private rttMs: number | null = null;
  private skewMs: number | null = null;
  private queueLagMs: number | null = null;
  private frameStamps: number[] = [];
  private flushIntervals: number[] = [];
  private at = 0;
  private listeners = new Set<() => void>();

  /** `sentAtMs` = when the ping left; `nowMs` = when the pong landed. */
  onPong(serverTs: number, sentAtMs: number, nowMs: number = Date.now()): void {
    if (!Number.isFinite(serverTs) || serverTs <= 0 || nowMs < sentAtMs) return;
    const rtt = nowMs - sentAtMs;
    const skew = serverTs - (sentAtMs + rtt / 2);
    this.rttMs = this.rttMs == null ? rtt : this.rttMs * 0.7 + rtt * 0.3;
    this.skewMs = this.skewMs == null ? skew : this.skewMs * 0.7 + skew * 0.3;
    this.at = nowMs;
    this.emit();
  }

  /** An `alive` beat carrying the gateway's send stamp. */
  onAlive(serverTs: number, nowMs: number = Date.now()): void {
    if (!Number.isFinite(serverTs) || serverTs <= 0 || this.skewMs == null) return;
    this.queueLagMs = Math.max(0, Math.round(nowMs - (serverTs - this.skewMs)));
    this.at = nowMs;
    this.emit();
  }

  /** A market or book frame reached the dispatcher. */
  onFrame(nowMs: number = Date.now()): void {
    this.frameStamps.push(nowMs);
    // Trim lazily: the array only grows between snapshots.
    if (this.frameStamps.length > 4_096) this.trim(nowMs);
  }

  /** The store flushed; `intervalMs` since the previous flush. */
  onFlush(intervalMs: number): void {
    if (!Number.isFinite(intervalMs) || intervalMs < 0) return;
    this.flushIntervals.push(intervalMs);
    if (this.flushIntervals.length > FLUSH_RING) this.flushIntervals.splice(0, this.flushIntervals.length - FLUSH_RING);
  }

  snapshot(nowMs: number = Date.now()): GatewayHealthSnapshot {
    this.trim(nowMs);
    const fps = this.frameStamps.length / (FPS_WINDOW_MS / 1_000);
    let p95: number | null = null;
    if (this.flushIntervals.length > 0) {
      const sorted = [...this.flushIntervals].sort((a, b) => a - b);
      p95 = Math.round(sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))]!);
    }
    return {
      rttMs: this.rttMs == null ? null : Math.round(this.rttMs),
      skewMs: this.skewMs == null ? null : Math.round(this.skewMs),
      queueLagMs: this.queueLagMs,
      framesPerSec: Math.round(fps),
      flushP95Ms: p95,
      at: this.at,
    };
  }

  reset(): void {
    this.rttMs = this.skewMs = this.queueLagMs = null;
    this.frameStamps = [];
    this.flushIntervals = [];
    this.at = 0;
    this.emit();
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  }

  private trim(nowMs: number): void {
    const cutoff = nowMs - FPS_WINDOW_MS;
    let i = 0;
    while (i < this.frameStamps.length && this.frameStamps[i]! < cutoff) i += 1;
    if (i > 0) this.frameStamps.splice(0, i);
  }

  private emit(): void { for (const fn of this.listeners) fn(); }
}

/** The tab's one meter (one gateway socket per tab). */
export const gatewayHealth = new GatewayHealthMeter();

/** Rough "the tab is behind" thresholds for the status bar. */
export const QUEUE_LAG_WARN_MS = 250;
export const FLUSH_P95_WARN_MS = 100;
